import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { withApollo } from 'react-apollo';

import translatePropTypes from '../../../translatePropTypes';
import withEditSession from '../../containers/EditSessionProvider/withEditSession';
import Tree from '../../dataViews/NextTree/Tree/Tree';
import ContentBox from '../../utils/ContentBox';
import FormTitle from '../../utils/FormTitle';
import withTranslations from '../../utils/Translations/withTranslations';
import { CommonDynamicTree } from '../BasicTrees';
import TreeContentWrapper from '../BasicTrees/TreeContentWrapper';
import {
  getTreeLayoutConfig,
  TreeLayoutTypeEnum,
  treeLayoutProps,
} from '../BasicTrees/TreeLayoutConfig';
import DynamicTreeBody from './DynamicTreeBody';
import DynamicTreeDefaultFilter from './DynamicTreeDefaultFilter';

@withApollo
@withTranslations
@withEditSession
export default class DynamicTree extends CommonDynamicTree {
  static propTypes = {
    ...CommonDynamicTree.propTypes,

    title: PropTypes.string,
    hasTitle: PropTypes.bool,
    paragraph: PropTypes.string,
    dataCy: PropTypes.string,
    editNodeComponent: PropTypes.element,

    filter: PropTypes.shape({
      value: PropTypes.object,
      onChange: PropTypes.func,
      component: PropTypes.func,
      hasMultiStatus: PropTypes.bool,
      hasOmitDeleteStatus: PropTypes.bool,
      hasSearchFieldOnly: PropTypes.bool,
    }),
    ...treeLayoutProps,
    ...translatePropTypes,
  };

  static defaultProps = {
    ...CommonDynamicTree.defaultProps,

    title: null,
    hasTitle: false,
    paragraph: null,
    dataCy: undefined,
    editNodeComponent: null,

    filter: {},
  };

  get filter() {
    const {
      hasFilter,
      filter: {
        component,
        hasMultiStatus,
        hasOmitDeleteStatus,
        hasSearchFieldOnly,
      },
      treeLayoutType,
    } = this.props;

    if (!hasFilter) {
      return null;
    }

    const FilterComponent = component || DynamicTreeDefaultFilter;

    const searchFieldWidthClassName = getTreeLayoutConfig(
      this.props.treeLayoutType || TreeLayoutTypeEnum.TREE_TABBED_PAGE,
    ).searchFieldWidthClassName;

    return (
      <FilterComponent
        hasMultiStatus={hasMultiStatus}
        hasOmitDeleteStatus={hasOmitDeleteStatus}
        hasSearchFieldOnly={hasSearchFieldOnly}
        searchFieldWidthClassName={searchFieldWidthClassName}
        treeLayoutType={treeLayoutType}
        value={this.props.filter.value || this.state.filter}
        onChange={this.handleFilterChange}
      />
    );
  }

  get title() {
    const { title, hasTitle, syntheticRootNodeName } = this.props;

    if (!hasTitle) {
      return null;
    }

    return _.isString(title) || !title ? (
      <FormTitle title={title || syntheticRootNodeName} />
    ) : (
      title
    );
  }

  renderContent(plugins) {
    const { children, editNodeComponent, treeLayoutType } = this.props;
    const { adapter } = this.state;

    const body = this.getTreeBody(plugins);

    return children ? (
      children(body, this.filter, editNodeComponent, plugins, adapter, {
        title: this.title,
      })
    ) : (
      <>
        {this.title}
        {this.filter}
        <TreeContentWrapper
          EditComponent={editNodeComponent}
          isNodeEditVisible={!!editNodeComponent}
          treeLayoutType={treeLayoutType}
        >
          {body}
        </TreeContentWrapper>
      </>
    );
  }

  getTreeBody = plugins => {
    const {
      tree,
      permission,
      gqlQueries,
      filter: { value: filterValue },
      value,
      isSyntheticRootNodeAlwaysVisible,
      isEditSessionActive,
      paragraph,
      dataCy,
    } = this.props;

    const { filter: stateFilterValue, rootModels, adapter } = this.state;

    return (
      <ContentBox
        hasLimitScrollableContentHeight
        isScrolled
        dataCy={dataCy}
        paragraph={paragraph}
      >
        <DynamicTreeBody
          filter={_.omit(filterValue || stateFilterValue, 'searchQuery')}
          gqlQueries={gqlQueries}
          isSyntheticRootNodeAlwaysVisible={isSyntheticRootNodeAlwaysVisible}
          permission={permission}
          resolveNodeId={this.resolveNodeId}
          rootModels={rootModels}
          searchQuery={this.prepareSearchQuery(
            (filterValue && filterValue.searchQuery) ||
              stateFilterValue.searchQuery,
          )}
          selection={value}
          tree={tree}
          treeAdapter={adapter}
          treeContext={{
            isEditSessionActive,
            getMainTreeChildrenCounts: this.getMainTreeChildrenCounts,
          }}
          treePlugins={plugins}
        />
      </ContentBox>
    );
  };

  render() {
    const {
      value,
      onSelectionChange,
      tree,
      subtreeFilter,
      isEditSessionActive,
      filter: { value: filterValue },
    } = this.props;
    const { rootModels, adapter, filter: stateFilter } = this.state;

    const plugins = this.makePlugins();

    return (
      <Tree
        adapter={adapter}
        context={{
          isEditSessionActive,
          getMainTreeChildrenCounts: this.getMainTreeChildrenCounts,
          setMainTreeChildrenCounts: this.setMainTreeChildrenCounts,
        }}
        filter={subtreeFilter || filterValue || stateFilter}
        models={rootModels}
        plugins={plugins}
        selection={value}
        tree={tree}
        onSelectionChange={onSelectionChange}
      >
        {this.renderContent(plugins)}
      </Tree>
    );
  }
}
